import React from "react";
import { useTranslation } from "react-i18next";
import { map } from "lodash";
import Text from "@/components/ui/text";
import KPIsContainer from "@/components/controls/KPIsContainer";
import IcpFitValue from "@/components/controls/DetailedView/ICPFit/IcpFitValue";
import { IcpFitSubject } from "@/entities/icpFit/types";
import IcpFitDetailsPopover from "@/components/controls/DetailedView/ICPFit/IcpFitDetailsPopover";
import { CompanyData } from "@/pages/companies/types";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";

interface Props {
  company?: CompanyData;
  containerClassName?: string;
  isQuickView?: boolean;
}

const CompanyKPIs: React.FC<Props> = ({
  company,
  containerClassName,
  isQuickView
}) => {
  const { t } = useTranslation();
  const { enabled: isIcpFitEnabled, loading: isIcpFitFlagLoading } =
    useFeatureFlag(FeatureFlags.CRM_5676_ICP);

  if (!company || isIcpFitFlagLoading) return null;
  if (!isIcpFitEnabled) return null;

  const kpis: { key: string; subtitle: string; render: () => JSX.Element }[] = [
    {
      key: "icpFit",
      subtitle: t("companies.kpis.companyIcpFit"),
      render: () => (
        <div className="flex items-center gap-1 min-w-0">
          <IcpFitValue
            value={company.icpFit}
            isQuickView={isQuickView}
            subject={IcpFitSubject.COMPANY}
            emptyVariant={isQuickView ? "lgLabel" : "lgSubtitleH1"}
          />
          <IcpFitDetailsPopover
            icpFit={company.icpFit}
            bullets={company.icpMetadata?.bullets}
            previewLabel={t("companies.kpis.companyIcpFit")}
          />
        </div>
      )
    }
  ];

  return (
    <KPIsContainer className={containerClassName}>
      {map(kpis, ({ key, subtitle, render }) => (
        <div key={key} className="flex flex-col gap-1 p-3 min-w-0">
          <div className="flex items-center h-7">{render()}</div>
          <Text variant="bodySecondary">{subtitle}</Text>
        </div>
      ))}
    </KPIsContainer>
  );
};

export default CompanyKPIs;
